import React, { useEffect, useMemo, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { ArrowPathIcon, BellAlertIcon, LinkIcon, XMarkIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import PageHeader from '../../components/ui/PageHeader';
import SectionCard from '../../components/ui/SectionCard';
import EmptyState from '../../components/ui/EmptyState';
import { ADMIN_PAGE_WIDTH, cx } from '../../components/ui/utils';
import { db } from '../../firebase';
import { syncHostingPackages, linkHostingPackage, unlinkHostingPackage, syncHostingRenewalTasks } from '../../services/hostingApi';
import { fetchAdminClientsSnapshot } from '../../services/adminClientsApi';

const REMINDER_WINDOW_DAYS = 30;

const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Sugiere el cliente cuyo businessName se parece más al dominio — solo una
// pista visual, el admin siempre confirma o corrige antes de vincular.
function suggestClientId(domains, clients) {
  const domainKey = normalize((domains[0] || '').split('.')[0]);
  if (!domainKey) return null;
  const match = clients.find((c) => {
    const nameKey = normalize(c.businessName || c.name || '');
    return nameKey && (nameKey.includes(domainKey) || domainKey.includes(nameKey));
  });
  return match?.id || null;
}

function daysUntil(date) {
  const ms = date.getTime() - Date.now();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

export default function AdminHosting() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncingRenewals, setSyncingRenewals] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({}); // packageId -> { clientId, renewalDate, autoRenew }
  const [savingId, setSavingId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [reminderLog, setReminderLog] = useState([]);

  useEffect(() => {
    const q = query(collection(db, 'hosting_packages'), orderBy('name', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setPackages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => { setError(err.message || 'No se pudieron cargar los paquetes de hosting.'); setLoading(false); });
    return unsub;
  }, []);

  // Evidencia real de cada recordatorio mandado — quién, cuándo, y si de
  // verdad se entregó o falló. Sin esto solo quedaba en logs de servidor que
  // nadie podía ver.
  useEffect(() => {
    const q = query(collection(db, 'hosting_reminder_log'), orderBy('sentAt', 'desc'), limit(50));
    const unsub = onSnapshot(q, (snap) => {
      setReminderLog(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => {});
    return unsub;
  }, []);

  useEffect(() => {
    fetchAdminClientsSnapshot().then((res) => setClients(res.clients || [])).catch(() => {});
  }, []);

  const clientsById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);

  // Próximas renovaciones — se calculan directo de `clients` (ahí vive la
  // fecha real), no de hosting_packages.
  const upcomingRenewals = useMemo(() => {
    return clients
      .filter((c) => c.hostingRenewalDate && c.status !== 'deleted')
      .map((c) => {
        const date = c.hostingRenewalDate?.toDate ? c.hostingRenewalDate.toDate() : new Date(c.hostingRenewalDate);
        return { client: c, date, days: daysUntil(date) };
      })
      .filter((r) => r.days <= REMINDER_WINDOW_DAYS)
      .sort((a, b) => a.days - b.days);
  }, [clients]);

  const runSync = async () => {
    setSyncing(true); setError(''); setNotice('');
    try {
      const result = await syncHostingPackages();
      setNotice(`Sincronizado: ${result.total} paquetes traídos de 20i.`);
    } catch (e) { setError(e.message || 'No se pudo sincronizar con 20i.'); }
    finally { setSyncing(false); }
  };

  const syncRenewalsToAgenda = async () => {
    setSyncingRenewals(true); setError(''); setNotice('');
    try {
      const result = await syncHostingRenewalTasks();
      setNotice(`${result.synced || 0} renovaciones se asignaron a Yamileth y se enviaron a Agenda/Google Calendar.${result.failed ? ` ${result.failed} requieren revisión.` : ''}`);
    } catch (e) { setError(e.message || 'No se pudieron enviar las renovaciones a Agenda.'); }
    finally { setSyncingRenewals(false); }
  };

  const changeDraft = (packageId, field, value) => {
    setDrafts((prev) => ({ ...prev, [packageId]: { ...prev[packageId], [field]: value } }));
  };

  const getDraft = (pkg) => {
    const existing = drafts[pkg.id];
    if (existing) return existing;
    const suggested = !pkg.linkedClientId ? suggestClientId(pkg.domains || [], clients) : pkg.linkedClientId;
    return { clientId: suggested || '', renewalDate: '', billingFrequency: 'annual', autoRenew: true, amount: '' };
  };

  const saveLink = async (pkg) => {
    const draft = getDraft(pkg);
    if (!draft.clientId) { setError('Selecciona un cliente antes de vincular.'); return; }
    setSavingId(pkg.id); setError(''); setNotice('');
    try {
      await linkHostingPackage({ packageId: pkg.id, clientId: draft.clientId, renewalDate: draft.renewalDate, billingFrequency: draft.billingFrequency, autoRenew: draft.autoRenew, amount: draft.amount || undefined });
      setNotice(`${pkg.domains?.[0] || pkg.name} vinculado.`);
      setDrafts((prev) => { const next = { ...prev }; delete next[pkg.id]; return next; });
      fetchAdminClientsSnapshot().then((res) => setClients(res.clients || [])).catch(() => {});
    } catch (e) { setError(e.message || 'No se pudo vincular.'); }
    finally { setSavingId(null); }
  };

  const removeLink = async (pkg) => {
    setSavingId(pkg.id); setError(''); setNotice('');
    try {
      await unlinkHostingPackage({ packageId: pkg.id });
      setNotice(`${pkg.domains?.[0] || pkg.name} desvinculado.`);
      fetchAdminClientsSnapshot().then((res) => setClients(res.clients || [])).catch(() => {});
    } catch (e) { setError(e.message || 'No se pudo desvincular.'); }
    finally { setSavingId(null); }
  };

  const jumpToPackage = (linkedClientId) => {
    const pkg = packages.find((p) => p.linkedClientId === linkedClientId);
    if (!pkg) return;
    setHighlightId(pkg.id);
    document.getElementById(`hosting-pkg-${pkg.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => setHighlightId(null), 2500);
  };

  return <main className={`${ADMIN_PAGE_WIDTH} mx-auto px-4 py-6 sm:px-6`}>
    <PageHeader
      breadcrumb={[{ label: 'Hosting' }]}
      title="Hosting"
      description="Catálogo real de dominios en 20i, vinculados a tu CRM. La fecha de renovación se guarda aquí — 20i no la da por API."
      actions={<div className="flex flex-wrap gap-2">
        <button type="button" onClick={syncRenewalsToAgenda} disabled={syncingRenewals} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"><BellAlertIcon className={cx('h-4 w-4', syncingRenewals && 'animate-pulse')} />{syncingRenewals ? 'Enviando…' : 'Enviar renovaciones a Agenda'}</button>
        <button type="button" onClick={runSync} disabled={syncing} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><ArrowPathIcon className={cx('h-4 w-4', syncing && 'animate-spin')} />{syncing ? 'Sincronizando…' : 'Sincronizar con 20i'}</button>
      </div>}
    />

    {error && <p role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
    {notice && <p className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">{notice}</p>}

    {upcomingRenewals.length > 0 && (
      <SectionCard title="Próximas renovaciones" subtitle={`Clientes con vencimiento en los próximos ${REMINDER_WINDOW_DAYS} días.`} className="mt-5">
        <div className="space-y-2">
          {upcomingRenewals.map(({ client, date, days }) => (
            <button
              key={client.id}
              type="button"
              onClick={() => jumpToPackage(client.hostingPackageId)}
              className={cx(
                'flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left text-sm',
                days <= 7 ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'
              )}
            >
              <span className="flex items-center gap-2 font-semibold text-slate-800">
                <BellAlertIcon className={cx('h-4 w-4', days <= 7 ? 'text-rose-500' : 'text-amber-500')} />
                {client.businessName || client.name}
                <span className="font-normal text-slate-400">— {(client.hostingDomains || []).join(', ')}</span>
              </span>
              <span className={cx('font-bold', days <= 7 ? 'text-rose-600' : 'text-amber-700')}>
                {days <= 0 ? 'Vencido' : `en ${days} días`} · {date.toLocaleDateString('es-PR')}
              </span>
            </button>
          ))}
        </div>
      </SectionCard>
    )}

    <SectionCard title="Paquetes de hosting" subtitle={`${packages.length} dominios en tu cuenta reseller de 20i.`} className="mt-5" bodyClassName="p-0">
      {loading ? <p className="p-4 text-xs text-slate-400">Cargando…</p>
        : !packages.length ? <EmptyState label="Todavía no hay nada sincronizado. Dale a 'Sincronizar con 20i'." />
        : <div className="divide-y divide-slate-100">
          {packages.map((pkg) => {
            const draft = getDraft(pkg);
            const linkedClient = pkg.linkedClientId ? clientsById[pkg.linkedClientId] : null;
            return (
              <div key={pkg.id} id={`hosting-pkg-${pkg.id}`} className={cx('p-4 transition-colors', highlightId === pkg.id && 'bg-amber-50')}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800">{pkg.domains?.[0] || pkg.name}</p>
                    {(pkg.domains || []).length > 1 && <p className="text-xs text-slate-400">+{pkg.domains.length - 1} subdominios</p>}
                    <p className="mt-0.5 text-xs text-slate-400">{pkg.packageTypeName} · {pkg.enabled ? 'Activo' : 'Inactivo'}</p>
                  </div>

                  {linkedClient ? (
                    <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
                      <div>
                        <p className="font-semibold text-emerald-800">{linkedClient.businessName || linkedClient.name}</p>
                        {linkedClient.hostingRenewalDate && (
                          <p className="text-xs text-emerald-700">
                            Renueva: {(linkedClient.hostingRenewalDate.toDate ? linkedClient.hostingRenewalDate.toDate() : new Date(linkedClient.hostingRenewalDate)).toLocaleDateString('es-PR')}
                            {' · '}{linkedClient.hostingBillingFrequency === 'monthly' ? 'Mensual' : 'Anual'}
                          </p>
                        )}
                      </div>
                      <button type="button" onClick={() => removeLink(pkg)} disabled={savingId === pkg.id} className="text-rose-500 hover:text-rose-700" title="Desvincular"><XMarkIcon className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <select value={draft.clientId} onChange={(e) => changeDraft(pkg.id, 'clientId', e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm">
                        <option value="">Selecciona cliente…</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.businessName || c.name || c.email}</option>)}
                      </select>
                      <input type="date" value={draft.renewalDate} onChange={(e) => changeDraft(pkg.id, 'renewalDate', e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
                      <select value={draft.billingFrequency} onChange={(e) => changeDraft(pkg.id, 'billingFrequency', e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" title="Cada cuánto se cobra — cambia cuándo se manda el recordatorio">
                        <option value="annual">Anual</option>
                        <option value="monthly">Mensual</option>
                      </select>
                      <input type="number" min="0" step="0.01" placeholder="Monto $" value={draft.amount} onChange={(e) => changeDraft(pkg.id, 'amount', e.target.value)} className="w-24 rounded-lg border border-slate-200 px-2 py-1.5 text-sm" title="Opcional — si lo pones, se genera la factura sola cuando se acerque la renovación" />
                      <button type="button" onClick={() => saveLink(pkg)} disabled={savingId === pkg.id} className="inline-flex items-center gap-1 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"><LinkIcon className="h-3.5 w-3.5" />Vincular</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>}
    </SectionCard>

    <SectionCard title="Historial de recordatorios enviados" subtitle="Evidencia real de cada envío — quién, cuándo, y si se entregó o falló." className="mt-5" bodyClassName="p-0">
      {reminderLog.length === 0 ? (
        <EmptyState label="Todavía no se ha mandado ningún recordatorio." />
      ) : (
        <div className="divide-y divide-slate-100">
          {reminderLog.map((log) => {
            const sentDate = log.sentAt?.toDate ? log.sentAt.toDate() : null;
            const renewalDate = log.renewalDate?.toDate ? log.renewalDate.toDate() : null;
            return (
              <div key={log.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800">{log.businessName}</p>
                  <p className="text-xs text-slate-400">
                    Aviso de {log.thresholdDays <= 1 ? '1 día' : `${log.thresholdDays} días`}
                    {renewalDate && ` · renovaba ${renewalDate.toLocaleDateString('es-PR')}`}
                    {sentDate && ` · enviado ${sentDate.toLocaleString('es-PR')}`}
                  </p>
                  {log.clientEmail && <p className="text-xs text-slate-400">{log.clientEmail}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {log.invoiceId && (
                    <a href={`/factura/${log.clientId}/${log.invoiceId}`} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-50">Ver factura</a>
                  )}
                  {log.emailSuccess ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                      <CheckCircleIcon className="h-4 w-4" />Entregado
                    </span>
                  ) : (
                    <span title={log.emailError || ''} className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-700">
                      <ExclamationCircleIcon className="h-4 w-4" />Falló
                    </span>
                  )}
                  {log.emailSuccess && (
                    log.opened ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-700">
                        <CheckCircleIcon className="h-4 w-4" />{log.clicked ? 'Abierto y con clic' : 'Abierto'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
                        Sin abrir todavía
                      </span>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  </main>;
}
